import { useState } from 'react';
import {
  MAX_PRICE,
  MIN_PRICE,
  PRICE_MESSAGE_KEY,
  isPriceOutOfBounds,
  priceMessageParams,
} from '~/utils/moneyBounds';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/overlays';
import { Button } from '~/components/ui/buttons/Button';
import { Input } from '~/components/ui/inputs/Input';
import { TextArea } from '~/components/ui/inputs/TextArea';
import { SearchableSelect } from '~/components/ui/inputs/SearchableSelect';
import { CurrencySelector } from '~/components/ui/inputs/CurrencySelector';
import {
  useCreateShippingRoute,
  useRequestShippingRoute,
  useUpdateShippingRoute,
  useGetShippingRouteCategories,
} from '~/api/logistics';
import type {
  GetShippingRoutesQuery,
  GetPortsQuery,
  LogisticsIncoterm,
  ShippingRouteStatus,
  Currency,
} from '~/graphql/graphql';
import { type ClientError, extractGraphQLErrorMessages } from '~/utils';

type ShippingRoute =
  GetShippingRoutesQuery['shippingRoutes']['content'][number];
type Port = GetPortsQuery['ports'][number];

export type ModalMode = 'create' | 'request' | 'edit';

interface ShippingRouteModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: ModalMode;
  route?: ShippingRoute;
  ports: Port[];
}

const COMBO_INCOTERMS: LogisticsIncoterm[] = [
  'FCA_COMBO',
  'DDP_COMBO',
  'DAP_COMBO',
];
const SEAPORT_TYPES = ['SEAPORT'];
const DELIVERY_TYPES = ['CY', 'RAMP', 'DOOR'];

const INCOTERM_OPTIONS: LogisticsIncoterm[] = [
  'FCA',
  'FOB',
  'CFR',
  'CIF',
  'DAP',
  'DDP',
  'FCA_COMBO',
  'DDP_COMBO',
  'DAP_COMBO',
];

const STATUS_CREATE_OPTIONS: ShippingRouteStatus[] = [
  'ACTIVE',
  'INACTIVE',
  'REQUESTED',
];
const STATUS_ALL_OPTIONS: ShippingRouteStatus[] = [
  'ACTIVE',
  'INACTIVE',
  'REQUESTED',
  'REQUEST_DENIED',
];

type MoneyState = { amount: string; currency: string };

interface FormState {
  portOfLoadingId: string;
  originDeliveryId: string;
  portOfDischargeId: string;
  destinationDeliveryId: string;
  incotermLoading: string;
  incotermDischarge: string;
  status: string;
  category: string;
  forwarder: string;
  forwarderEmail: string;
  transitDays: string;
  freeDays: string;
  freightCost: MoneyState;
  expirationDate: string;
  notes: string;
}

const emptyForm = (): FormState => ({
  portOfLoadingId: '',
  originDeliveryId: '',
  portOfDischargeId: '',
  destinationDeliveryId: '',
  incotermLoading: '',
  incotermDischarge: '',
  status: 'ACTIVE',
  category: '',
  forwarder: '',
  forwarderEmail: '',
  transitDays: '',
  freeDays: '',
  freightCost: { amount: '', currency: 'USD' },
  expirationDate: '',
  notes: '',
});

const formFromRoute = (route: ShippingRoute): FormState => ({
  portOfLoadingId: route.portOfLoading.id,
  originDeliveryId: route.originDelivery?.id ?? '',
  portOfDischargeId: route.portOfDischarge.id,
  destinationDeliveryId: route.destinationDelivery?.id ?? '',
  incotermLoading: route.incotermLoading ?? '',
  incotermDischarge: route.incotermDischarge ?? '',
  status: route.status,
  category: route.category ?? '',
  forwarder: route.forwarder ?? '',
  forwarderEmail: route.forwarderEmail ?? '',
  transitDays: route.transitDays != null ? String(route.transitDays) : '',
  freeDays: route.freeDays != null ? String(route.freeDays) : '',
  freightCost: {
    amount:
      route.freightCost?.amount != null ? String(route.freightCost.amount) : '',
    currency: route.freightCost?.currency ?? 'USD',
  },
  expirationDate: route.expirationDate
    ? new Date(route.expirationDate).toISOString().split('T')[0]
    : '',
  notes: route.notes ?? '',
});

export function ShippingRouteModal({
  isOpen,
  onClose,
  mode,
  route,
  ports,
}: ShippingRouteModalProps) {
  const { t } = useTranslation('logistics');
  const [form, setForm] = useState<FormState>(() =>
    mode === 'edit' && route ? formFromRoute(route) : emptyForm()
  );

  const { mutate: createRoute, isPending: isCreating } =
    useCreateShippingRoute();
  const { mutate: requestRoute, isPending: isRequesting } =
    useRequestShippingRoute();
  const { mutate: updateRoute, isPending: isUpdating } =
    useUpdateShippingRoute();
  const { data: categoriesData } = useGetShippingRouteCategories();
  const categories = categoriesData?.shippingRouteCategories ?? [];

  const isPending = isCreating || isRequesting || isUpdating;

  const seaPorts = ports.filter((p) => SEAPORT_TYPES.includes(p.type));
  const deliveryPoints = ports.filter((p) => DELIVERY_TYPES.includes(p.type));

  const toPortOptions = (list: Port[]) =>
    list.map((p) => ({ value: p.id, label: p.name }));

  const set = (field: keyof FormState, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const setMoney = (
    field: 'freightCost',
    key: 'amount' | 'currency',
    value: string
  ) =>
    setForm((prev) => ({ ...prev, [field]: { ...prev[field], [key]: value } }));

  const toMoneyInput = (m: MoneyState) => {
    const amount = parseFloat(m.amount);
    if (isNaN(amount) || m.amount === '') return undefined;
    return { amount, currency: (m.currency || 'USD') as Currency };
  };

  const validate = (): boolean => {
    if (mode !== 'edit') {
      if (!form.portOfLoadingId || !form.portOfDischargeId) {
        toast.error(t('logistics.common.required_fields_error'));
        return false;
      }
    }
    if (
      !form.incotermLoading ||
      !form.incotermDischarge ||
      !form.expirationDate
    ) {
      toast.error(t('logistics.common.required_fields_error'));
      return false;
    }
    const freightAmount = parseFloat(form.freightCost.amount);
    const transitDays = parseInt(form.transitDays);
    const freeDays = parseInt(form.freeDays);
    if (
      (form.freightCost.amount !== '' && freightAmount < 0) ||
      (form.transitDays !== '' && transitDays < 0) ||
      (form.freeDays !== '' && freeDays < 0)
    ) {
      toast.error(t('logistics.common.negative_value_error'));
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    if (isPriceOutOfBounds(parseFloat(form.freightCost.amount))) {
      toast.error(t(PRICE_MESSAGE_KEY, priceMessageParams));
      return;
    }

    const expirationDate = form.expirationDate
      ? new Date(form.expirationDate).toISOString()
      : undefined;

    const onSuccess = () => {
      const msgKey =
        mode === 'create'
          ? 'success_add'
          : mode === 'request'
            ? 'success_request'
            : 'success_edit';
      toast.success(t(`logistics.shipping_routes.modal.${msgKey}`));
      onClose();
    };

    const onError = (error: ClientError) =>
      toast.error(extractGraphQLErrorMessages(error).join(', '));

    if (mode === 'edit' && route) {
      updateRoute(
        {
          id: route.id,
          input: {
            status: (form.status as ShippingRouteStatus) || undefined,
            category: form.category || undefined,
            incotermLoading:
              (form.incotermLoading as LogisticsIncoterm) || undefined,
            incotermDischarge:
              (form.incotermDischarge as LogisticsIncoterm) || undefined,
            forwarder: form.forwarder || undefined,
            forwarderEmail: form.forwarderEmail.trim(),
            transitDays: form.transitDays
              ? parseInt(form.transitDays)
              : undefined,
            freeDays: form.freeDays ? parseInt(form.freeDays) : undefined,
            freightCost: toMoneyInput(form.freightCost),
            expirationDate: expirationDate || undefined,
            notes: form.notes || undefined,
          },
        },
        { onSuccess, onError }
      );
      return;
    }

    const createInput = {
      portOfLoadingId: form.portOfLoadingId,
      portOfDischargeId: form.portOfDischargeId,
      originDeliveryId: form.originDeliveryId || undefined,
      destinationDeliveryId: form.destinationDeliveryId || undefined,
      incotermLoading: form.incotermLoading as LogisticsIncoterm,
      incotermDischarge: form.incotermDischarge as LogisticsIncoterm,
      status:
        mode === 'request'
          ? ('REQUESTED' as ShippingRouteStatus)
          : (form.status as ShippingRouteStatus) || undefined,
      category: form.category || undefined,
      forwarder: form.forwarder || undefined,
      forwarderEmail: form.forwarderEmail.trim(),
      transitDays: form.transitDays ? parseInt(form.transitDays) : undefined,
      freeDays: form.freeDays ? parseInt(form.freeDays) : undefined,
      freightCost: toMoneyInput(form.freightCost),
      expirationDate: expirationDate || undefined,
      notes: form.notes || undefined,
    };

    if (mode === 'request') {
      requestRoute({ input: createInput }, { onSuccess, onError });
    } else {
      createRoute({ input: createInput }, { onSuccess, onError });
    }
  };

  const titleKey =
    mode === 'create'
      ? 'add_title'
      : mode === 'request'
        ? 'request_title'
        : 'edit_title';
  const submitKey =
    mode === 'create'
      ? 'submit_add'
      : mode === 'request'
        ? 'submit_request'
        : 'submit_edit';

  const statusOptions =
    mode === 'edit' ? STATUS_ALL_OPTIONS : STATUS_CREATE_OPTIONS;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-full max-w-xl">
      <div className="flex flex-col gap-6 p-8 bg-white rounded-lg">
        <h2 className="text-lg font-semibold text-neut-prim">
          {t(`logistics.shipping_routes.modal.${titleKey}`)}
        </h2>

        {/* Route Details */}
        <section className="flex flex-col gap-4">
          <h3 className="text-sm font-medium text-neut-600 uppercase tracking-wide">
            {t('logistics.shipping_routes.modal.route_details_section')}
          </h3>

          {mode !== 'request' && (
            <div className="w-64">
              <SearchableSelect
                label={t('logistics.shipping_routes.modal.status')}
                name="status"
                value={form.status}
                onChange={(_name, value) => set('status', value)}
                options={statusOptions.map((s) => ({
                  value: s,
                  label: t(`logistics.shipping_routes.status.${s}`),
                }))}
              />
            </div>
          )}

          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-40">
              <Input
                label={t('logistics.shipping_routes.modal.freight_cost')}
                name="freightCostAmount"
                type="number"
                min={MIN_PRICE}
                max={MAX_PRICE}
                value={form.freightCost.amount}
                onChange={(_name, value) =>
                  setMoney('freightCost', 'amount', value)
                }
              />
            </div>
            <div className="w-28">
              <CurrencySelector
                label={t('logistics.common.currency')}
                name="freightCostCurrency"
                value={form.freightCost.currency}
                onChange={(_name, value) =>
                  setMoney('freightCost', 'currency', value)
                }
              />
            </div>
            <div className="flex-1 min-w-40">
              <Input
                label={`${t('logistics.shipping_routes.modal.expiration_date')} *`}
                name="expirationDate"
                type="date"
                value={form.expirationDate}
                onChange={(_name, value) => set('expirationDate', value)}
              />
            </div>
          </div>
        </section>

        {/* Loading / Discharge — two column layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Loading */}
          <section className="flex flex-col gap-4">
            <h3 className="text-sm font-medium text-neut-600 uppercase tracking-wide">
              {t('logistics.shipping_routes.modal.loading_section')}
            </h3>
            <SearchableSelect
              label={`${t('logistics.shipping_routes.modal.incoterm_loading')} *`}
              name="incotermLoading"
              value={form.incotermLoading}
              onChange={(_name, value) => set('incotermLoading', value)}
              options={INCOTERM_OPTIONS.map((i) => ({ value: i, label: i }))}
            />
            {mode !== 'edit' && (
              <>
                <SearchableSelect
                  label={`${t('logistics.shipping_routes.modal.port_of_loading')} *`}
                  name="portOfLoadingId"
                  value={form.portOfLoadingId}
                  onChange={(_name, value) => set('portOfLoadingId', value)}
                  options={toPortOptions(seaPorts)}
                />
                {COMBO_INCOTERMS.includes(
                  form.incotermLoading as LogisticsIncoterm
                ) && (
                  <SearchableSelect
                    label={t('logistics.shipping_routes.modal.origin_delivery')}
                    name="originDeliveryId"
                    value={form.originDeliveryId}
                    onChange={(_name, value) => set('originDeliveryId', value)}
                    options={[
                      { value: '', label: '—' },
                      ...toPortOptions(deliveryPoints),
                    ]}
                  />
                )}
              </>
            )}
            {mode === 'edit' && (
              <p className="text-xs text-neut-sec">
                {route?.portOfLoading.code}
                {route?.originDelivery ? ` — ${route.originDelivery.code}` : ''}
              </p>
            )}
          </section>

          {/* Discharge */}
          <section className="flex flex-col gap-4">
            <h3 className="text-sm font-medium text-neut-600 uppercase tracking-wide">
              {t('logistics.shipping_routes.modal.discharge_section')}
            </h3>
            <SearchableSelect
              label={`${t('logistics.shipping_routes.modal.incoterm_discharge')} *`}
              name="incotermDischarge"
              value={form.incotermDischarge}
              onChange={(_name, value) => set('incotermDischarge', value)}
              options={INCOTERM_OPTIONS.map((i) => ({ value: i, label: i }))}
            />
            {mode !== 'edit' && (
              <>
                <SearchableSelect
                  label={`${t('logistics.shipping_routes.modal.port_of_discharge')} *`}
                  name="portOfDischargeId"
                  value={form.portOfDischargeId}
                  onChange={(_name, value) => set('portOfDischargeId', value)}
                  options={toPortOptions(seaPorts)}
                />
                {COMBO_INCOTERMS.includes(
                  form.incotermDischarge as LogisticsIncoterm
                ) && (
                  <SearchableSelect
                    label={t(
                      'logistics.shipping_routes.modal.destination_delivery'
                    )}
                    name="destinationDeliveryId"
                    value={form.destinationDeliveryId}
                    onChange={(_name, value) =>
                      set('destinationDeliveryId', value)
                    }
                    options={[
                      { value: '', label: '—' },
                      ...toPortOptions(deliveryPoints),
                    ]}
                  />
                )}
              </>
            )}
            {mode === 'edit' && (
              <p className="text-xs text-neut-sec">
                {route?.portOfDischarge.code}
                {route?.destinationDelivery
                  ? ` — ${route.destinationDelivery.code}`
                  : ''}
              </p>
            )}
          </section>
        </div>

        {/* Additional Details */}
        <section className="flex flex-col gap-4">
          <h3 className="text-sm font-medium text-neut-600 uppercase tracking-wide">
            {t('logistics.shipping_routes.modal.additional_section')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t('logistics.shipping_routes.modal.transit_days')}
              name="transitDays"
              type="number"
              min={0}
              value={form.transitDays}
              onChange={(_name, value) => set('transitDays', value)}
            />
            <Input
              label={t('logistics.shipping_routes.modal.free_days')}
              name="freeDays"
              type="number"
              min={0}
              value={form.freeDays}
              onChange={(_name, value) => set('freeDays', value)}
            />
            <SearchableSelect
              label={t('logistics.shipping_routes.modal.category')}
              name="category"
              value={form.category}
              onChange={(_name, value) => set('category', value)}
              options={[
                { value: '', label: '—' },
                ...categories.map((c) => ({ value: c, label: c })),
              ]}
              creatable
            />
            <Input
              label={t('logistics.shipping_routes.modal.forwarder')}
              name="forwarder"
              value={form.forwarder}
              onChange={(_name, value) => set('forwarder', value)}
            />
            <Input
              label={t('logistics.shipping_routes.modal.forwarder_email')}
              name="forwarderEmail"
              type="email"
              maxLength={320}
              data-testid="shipping-route-forwarder-email"
              value={form.forwarderEmail}
              onChange={(_name, value) => set('forwarderEmail', value)}
            />
            {(mode === 'edit' || form.forwarder.trim()) &&
              !form.forwarderEmail.trim() && (
                <p
                  data-testid="forwarder-email-missing-hint"
                  className="text-xs text-amber-700 mt-1"
                >
                  {t('logistics.shipping_routes.modal.forwarder_email_missing')}
                </p>
              )}
            <TextArea
              label={t('logistics.shipping_routes.modal.notes')}
              name="notes"
              value={form.notes}
              onChange={(_name, value) => set('notes', value)}
            />
          </div>
        </section>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-2 border-t border-neut-border">
          <Button onClick={onClose} disabled={isPending}>
            {t('logistics.common.cancel')}
          </Button>
          <Button
            data-testid="submit-shipping-route"
            onClick={handleSubmit}
            loading={isPending}
          >
            {t(`logistics.shipping_routes.modal.${submitKey}`)}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
